import React from "react";
import { Check, Flag, ListChecks, Milestone } from "lucide-react";
import { HomePanel, PanelEmpty } from "./HomePanel";
import { fmtDateHuman, untilLabel } from "@/portal/format";

const monthShort = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString("ro-RO", { month: "short", year: "numeric" }) : "");

/** Selectează ~5 repere în jurul celui curent: 2 finalizate, curentul, 2 următoare. */
function pickStages(list) {
  const ms = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.due_date || "").localeCompare(String(b.due_date || "")));
  const cur = ms.findIndex((m) => !m.completed);
  if (cur < 0) return ms.slice(-5).map((m) => ({ ...m, state: "done" }));
  const before = ms.slice(Math.max(0, cur - 2), cur).map((m) => ({ ...m, state: "done" }));
  const after = ms.slice(cur + 1, cur + 3).map((m) => ({ ...m, state: "next" }));
  return [...before, { ...ms[cur], state: "current" }, ...after];
}

function Dot({ state }) {
  if (state === "done") return <span className="h-5 w-5 rounded-full bg-cp-success text-white grid place-items-center shrink-0 ring-4 ring-white/70" aria-hidden="true"><Check className="h-3 w-3" strokeWidth={3} /></span>;
  if (state === "current") return <span className="h-5 w-5 rounded-full border-2 border-cp-accent bg-white grid place-items-center shrink-0 ring-4 ring-cp-accent-soft" aria-hidden="true"><span className="h-2 w-2 rounded-full bg-cp-accent" /></span>;
  return <span className="h-5 w-5 rounded-full border-2 border-cp-border-strong bg-white shrink-0 ring-4 ring-white/70" aria-hidden="true" />;
}

/** „Etape cheie” — reperele proiectului principal, în jurul etapei curente. */
export function KeyStagesCard({ project, milestones }) {
  const list = pickStages(milestones || []);
  return (
    <HomePanel icon={ListChecks} title="Etape cheie" to={project ? `/portal/proiecte/${project.id}?tab=calendar` : undefined} testid="home-stages">
      {list.length === 0 ? <PanelEmpty icon={Flag} text="Echipa Brisk va publica aici reperele proiectului." /> : (
        <ol className="relative mt-1" data-testid="home-stages-list">
          <span className="absolute left-[9px] top-2 bottom-3 w-px bg-cp-border" aria-hidden="true" />
          {list.map((m) => (
            <li key={m.id} className="relative flex items-start gap-3 pb-3.5 last:pb-0" data-testid={`stage-${m.state}`}>
              <Dot state={m.state} />
              <div className="min-w-0 -mt-0.5">
                <div className={`text-[13.5px] leading-snug ${m.state === "next" ? "font-medium text-cp-text-2" : "font-semibold text-cp-text"}`}>{m.title}</div>
                <div className={`text-[12px] mt-0.5 ${m.state === "current" ? "text-cp-accent font-medium" : "text-cp-text-3"}`}>
                  {m.state === "done" ? fmtDateHuman(m.completed_at || m.due_date) : m.state === "current" ? "În desfășurare" : `Estimat: ${monthShort(m.due_date)}`}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </HomePanel>
  );
}

/** „Repere contractuale următoare” — următoarele termene neîncheiate, pe toate proiectele. */
export function UpcomingMilestonesCard({ milestones, multiProject }) {
  const today = new Date().toISOString().slice(0, 10);
  const list = (milestones || []).filter((m) => !m.completed && (m.due_date || "") >= today).slice(0, 3);
  const first = list[0];
  return (
    <HomePanel icon={Milestone} title="Repere contractuale următoare" to={first ? `/portal/proiecte/${first.project_id}?tab=calendar` : undefined} testid="home-milestones">
      {list.length === 0 ? <PanelEmpty icon={Flag} text="Nu există repere viitoare publicate." /> : (
        <ol className="relative mt-1" data-testid="home-milestones-list">
          <span className="absolute left-[7px] top-2 bottom-3 w-px bg-cp-border" aria-hidden="true" />
          {list.map((m, i) => (
            <li key={m.id} className="relative flex items-start gap-3 pb-3.5 last:pb-0" data-testid={`milestone-next-${i}`}>
              <span className={`h-4 w-4 rounded-full shrink-0 mt-0.5 ring-4 ring-white/70 ${i === 0 ? "bg-cp-success" : "bg-white border-2 border-cp-border-strong"}`} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] font-semibold text-cp-text leading-snug">{m.title}</div>
                <div className="text-[12px] text-cp-text-3 mt-0.5">{fmtDateHuman(m.due_date)}{multiProject ? ` · ${m.project_name}` : ""}</div>
              </div>
              <span className="text-[12px] text-cp-text-2 font-medium whitespace-nowrap shrink-0">{untilLabel(m.due_date)}</span>
            </li>
          ))}
        </ol>
      )}
    </HomePanel>
  );
}
